import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  Component,
  Input,
  AfterViewInit,
  ChangeDetectorRef,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

import { SnAbilityService } from '@lib/ability';
import { SnWidget } from '@lib/api/entities';
import { SnOperationalViewsAPI } from '@lib/api/services';
import { SnFormFieldConfig } from '@lib/form';
import { t } from '@lib/translation';

@Component({
  selector: 'sn-twitter-embed-widget',
  templateUrl: './twitter-embed-widget.component.html',
  styleUrls: ['./twitter-embed-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TwitterEmbedWidgetComponent implements AfterViewInit, OnDestroy {
  @Input() public widget: SnWidget;
  @Input() public viewId: string;

  public src: string;
  public changeUrl: boolean;
  public changeTitle: boolean = false;
  public form: UntypedFormGroup = new UntypedFormGroup({});
  public formModel: any = {};
  public formFields: SnFormFieldConfig[] = [
    {
      key: 'embedUrl',
      type: 'input',
      templateOptions: {
        appearance: 'outline',
        floatLabel: 'always',
        placeholder: t('dashboard.operational.widgets.enterUrlPlaceholder'),
        required: true,
        label: t('dashboard.operational.widgets.twitterProfileUrlLabel'),
      },
    },
  ];
  public readonly canChange: boolean;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _cd: ChangeDetectorRef,
    private readonly _operationalViewsAPI: SnOperationalViewsAPI,
    private readonly _ability: SnAbilityService,
  ) {
    this.canChange = this._ability.can({ subject: 'dashboards/widgetobject', action: 'change' });
  }

  public ngAfterViewInit(): void {
    this._checkForUrl();
    this._cd.detectChanges();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public toggleChangeUrl(): void {
    this.changeUrl = !this.changeUrl;
    this._initTwitterWidget();
    this._cd.detectChanges();
  }

  public editTitle(): void {
    this.changeTitle = true;
  }

  public setTitle(value: boolean): void {
    this.changeTitle = value;
    this._cd.detectChanges();
  }

  public setUpdateTitle(value: string): void {
    this.widget.name = value;
    this._cd.detectChanges();
  }

  public submitForm(): void {
    if (!this.changeTitle) {
      this.src = null;
      this._operationalViewsAPI
        .updateViewWidget(this.viewId, this.widget.id, {
          meta: { embedUrl: this.form.value.embedUrl },
        })
        .pipe(takeUntil(this._destroy$))
        .subscribe((widget) => {
          this.widget.meta.embedUrl = widget.meta.embedUrl;
          this._cd.detectChanges();
          this.src = this.widget.meta.embedUrl;
          this.changeUrl = false;
          this._initTwitterWidget();
          this._cd.detectChanges();
        });
    } else {
      this.changeTitle = false;
      this._cd.detectChanges();
    }
  }

  private _checkForUrl(): void {
    if (this.widget.meta.embedUrl) {
      this.src = this.widget.meta.embedUrl;
      this._initTwitterWidget();
      this._cd.detectChanges();
    }
  }

  private _initTwitterWidget(): void {
    (<any>window).twttr = ((d, s, id) => {
      const js: any = d.createElement(s);
      const fjs = d.getElementsByTagName(s)[0];
      const t = (<any>window).twttr || {};
      if (d.getElementById(id)) {
        return t;
      }
      js.id = id;
      js.src = 'https://platform.twitter.com/widgets.js';
      fjs.parentNode.insertBefore(js, fjs);

      t._e = [];
      t.ready = (f: any) => {
        t._e.push(f);
      };
      return t;
    })(document, 'script', 'twitter-wjs');

    if ((<any>window).twttr.ready()) {
      (<any>window).twttr.widgets.load();
    }
  }
}
